import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, CheckboxField, PageHeader, SelectField, TextField } from '../../components/ui';
import { AdminOnly, WithBuilding } from './Guard';
import { BASE, SrBuilding, keys } from './api';

type Settings = SrBuilding['settings'];

const LOCALE = 'en-GB';

/** Building settings: the shared tablet, alerts, evacuation and marshal phones. Building administrators only. */
export function SettingsPage() {
  return (
    <AdminOnly>
      <PageHeader title={t('srb.settings.title')} description={t('srb.settings.desc')} />
      <WithBuilding>{({ building }) => <SettingsForms building={building} />}</WithBuilding>
    </AdminOnly>
  );
}

function SettingsForms({ building }: { building: SrBuilding }) {
  return (
    <div className="space-y-6">
      <MainForm building={building} />
      <PinForm pinSet={building.pinSet} />
    </div>
  );
}

function MainForm({ building }: { building: SrBuilding }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [s, setS] = useState<Settings>(building.settings);
  const [alertEmail, setAlertEmail] = useState(building.alertEmail);
  const [dirty, setDirty] = useState(false);
  const change = (fn: (d: Settings) => Settings) => {
    setS((cur) => fn(cur));
    setDirty(true);
  };
  const save = useMutation({
    mutationFn: () =>
      api.patch(`${BASE}/settings`, {
        body: { settings: s, alertEmail },
        schema: SrBuilding,
      }),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: keys.me });
      toast(t('srb.settings.saved'));
    },
  });
  const errs = fieldErrors(save.error);
  const num = (v: string, fallback: number) => (v === '' || Number.isNaN(Number(v)) ? fallback : Math.trunc(Number(v)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-6" noValidate>
      <Card title={t('srb.settings.kiosk')} description={t('srb.settings.kioskDesc')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t('srb.settings.welcome')}
            hint={t('srb.settings.welcomeHint')}
            maxLength={200}
            value={s.welcomeText[LOCALE] ?? ''}
            onChange={(e) => change((d) => ({ ...d, welcomeText: { ...d.welcomeText, [LOCALE]: e.target.value } }))}
          />
          <TextField
            label={t('srb.settings.privacy')}
            hint={t('srb.settings.privacyHint')}
            maxLength={200}
            value={s.privacyText[LOCALE] ?? ''}
            onChange={(e) => change((d) => ({ ...d, privacyText: { ...d.privacyText, [LOCALE]: e.target.value } }))}
          />
          <TextField
            type="number"
            min={0}
            max={100}
            label={t('srb.settings.pickerSearch')}
            hint={t('srb.settings.pickerSearchHint')}
            value={s.companyPickerSearchFrom}
            onChange={(e) => change((d) => ({ ...d, companyPickerSearchFrom: num(e.target.value, 0) }))}
          />
          <TextField
            type="number"
            min={0}
            max={600}
            label={t('srb.settings.idle')}
            hint={t('srb.settings.idleHint')}
            value={s.idleResetSeconds}
            onChange={(e) => change((d) => ({ ...d, idleResetSeconds: num(e.target.value, 0) }))}
          />
        </div>
        <CheckboxField
          className="mt-4"
          label={t('srb.settings.nfc')}
          hint={t('srb.settings.nfcHint')}
          checked={s.allowCompanyNfcEnrolment}
          onChange={(e) => change((d) => ({ ...d, allowCompanyNfcEnrolment: e.target.checked }))}
        />
      </Card>

      <Card title={t('srb.settings.alerts')} description={t('srb.settings.alertsDesc')}>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            type="email"
            label={t('srb.settings.alertEmail')}
            hint={t('srb.settings.alertEmailHint')}
            value={alertEmail}
            error={errs['alertEmail']}
            onChange={(e) => {
              setAlertEmail(e.target.value);
              setDirty(true);
            }}
          />
          <TextField
            type="number"
            min={0}
            max={1440}
            label={t('srb.settings.offlineMinutes')}
            hint={t('srb.settings.zeroOff')}
            value={s.alerts.offlineMinutes}
            onChange={(e) => change((d) => ({ ...d, alerts: { ...d.alerts, offlineMinutes: num(e.target.value, 0) } }))}
          />
          <TextField
            type="number"
            min={0}
            max={100}
            label={t('srb.settings.battery')}
            hint={t('srb.settings.zeroOff')}
            value={s.alerts.batteryPercent}
            onChange={(e) => change((d) => ({ ...d, alerts: { ...d.alerts, batteryPercent: num(e.target.value, 0) } }))}
          />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <CheckboxField label={t('srb.settings.printerFault')} checked={s.alerts.printerFault} onChange={(e) => change((d) => ({ ...d, alerts: { ...d.alerts, printerFault: e.target.checked } }))} />
          <CheckboxField label={t('srb.settings.appOutdated')} checked={s.alerts.appOutdated} onChange={(e) => change((d) => ({ ...d, alerts: { ...d.alerts, appOutdated: e.target.checked } }))} />
        </div>
      </Card>

      <Card title={t('srb.settings.evacuation')} description={t('srb.settings.evacuationDesc')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label={t('srb.settings.kioskStart')}
            hint={t('srb.settings.kioskStartHint')}
            value={s.evacuation.kioskStart}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, kioskStart: e.target.value as Settings['evacuation']['kioskStart'] } }))}
          >
            <option value="off">{t('srb.settings.kioskStart.off')}</option>
            <option value="pin">{t('srb.settings.kioskStart.pin')}</option>
            <option value="open">{t('srb.settings.kioskStart.open')}</option>
          </SelectField>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <TextField
            type="number"
            min={0}
            max={720}
            label={t('srb.settings.autoEndReal')}
            hint={t('srb.settings.autoEndHint')}
            value={s.evacuation.autoEndHours.real}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, autoEndHours: { ...d.evacuation.autoEndHours, real: num(e.target.value, 0) } } }))}
          />
          <TextField
            type="number"
            min={0}
            max={720}
            label={t('srb.settings.autoEndDrill')}
            hint={t('srb.settings.autoEndHint')}
            value={s.evacuation.autoEndHours.drill}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, autoEndHours: { ...d.evacuation.autoEndHours, drill: num(e.target.value, 0) } } }))}
          />
          <TextField
            type="number"
            min={0}
            max={720}
            label={t('srb.settings.longRunning')}
            hint={t('srb.settings.longRunningHint')}
            value={s.evacuation.longRunningAlertHours}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, longRunningAlertHours: num(e.target.value, 0) } }))}
          />
        </div>
        <div className="mt-4 space-y-3">
          <CheckboxField
            label={t('srb.settings.memberStarts')}
            hint={t('srb.settings.memberStartsHint')}
            checked={s.evacuation.memberStartsBuilding}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, memberStartsBuilding: e.target.checked } }))}
          />
          <CheckboxField
            label={t('srb.settings.memberDrills')}
            hint={t('srb.settings.memberDrillsHint')}
            checked={s.evacuation.memberDrillsBuilding}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, memberDrillsBuilding: e.target.checked } }))}
          />
          <CheckboxField
            label={t('srb.settings.groupLabel')}
            hint={t('srb.settings.groupLabelHint')}
            checked={s.evacuation.showGroupLabel}
            onChange={(e) => change((d) => ({ ...d, evacuation: { ...d.evacuation, showGroupLabel: e.target.checked } }))}
          />
        </div>
      </Card>

      <Card
        title={t('srb.settings.phones')}
        description={t('srb.settings.phonesDesc')}
        footer={
          <Button type="submit" variant="primary" disabled={!dirty || save.isPending}>
            {t('srb.settings.save')}
          </Button>
        }
      >
        <CheckboxField
          label={t('srb.settings.phonesOn')}
          hint={t('srb.settings.phonesOnHint')}
          checked={s.marshalPhones.enabled}
          onChange={(e) => change((d) => ({ ...d, marshalPhones: { ...d.marshalPhones, enabled: e.target.checked } }))}
        />
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextField
            type="number"
            min={1}
            max={60}
            label={t('srb.settings.refresh')}
            hint={t('srb.settings.refreshHint')}
            value={s.marshalPhones.refreshMinutes}
            disabled={!s.marshalPhones.enabled}
            onChange={(e) => change((d) => ({ ...d, marshalPhones: { ...d.marshalPhones, refreshMinutes: num(e.target.value, 1) } }))}
          />
          <TextField
            type="number"
            min={1}
            max={48}
            label={t('srb.settings.cacheExpiry')}
            hint={t('srb.settings.cacheExpiryHint')}
            value={s.marshalPhones.cacheExpiryHours}
            disabled={!s.marshalPhones.enabled}
            onChange={(e) => change((d) => ({ ...d, marshalPhones: { ...d.marshalPhones, cacheExpiryHours: num(e.target.value, 1) } }))}
          />
        </div>
        <div className="mt-4 space-y-3">
          <CheckboxField
            label={t('srb.settings.canStart')}
            hint={t('srb.settings.canStartHint')}
            checked={s.marshalPhones.canStart}
            disabled={!s.marshalPhones.enabled}
            onChange={(e) => change((d) => ({ ...d, marshalPhones: { ...d.marshalPhones, canStart: e.target.checked } }))}
          />
          <CheckboxField
            label={t('srb.settings.appLock')}
            hint={t('srb.settings.appLockHint')}
            checked={s.marshalPhones.requireAppLock}
            disabled={!s.marshalPhones.enabled}
            onChange={(e) => change((d) => ({ ...d, marshalPhones: { ...d.marshalPhones, requireAppLock: e.target.checked } }))}
          />
        </div>
        <div className="mt-4">
          <ErrorLine error={Object.keys(errs).length ? null : save.error} />
        </div>
      </Card>
    </form>
  );
}

function PinForm({ pinSet }: { pinSet: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [pin, setPin] = useState('');
  const save = useMutation({
    mutationFn: () => api.put(`${BASE}/evacuation-pin`, { body: { pin } }),
    onSuccess: async () => {
      setPin('');
      await qc.invalidateQueries({ queryKey: keys.me });
      toast(t('srb.pin.saved'));
    },
  });
  const errs = fieldErrors(save.error);
  const valid = /^\d{4,8}$/.test(pin);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
      noValidate
    >
      <Card
        title={
          <span className="inline-flex items-center gap-2">
            {t('srb.pin.title')}
            <Badge tone={pinSet ? 'good' : 'warn'}>{pinSet ? t('srb.pin.set') : t('srb.pin.notSet')}</Badge>
          </span>
        }
        description={t('srb.pin.desc')}
        footer={
          <Button type="submit" variant="primary" disabled={!valid || save.isPending}>
            {pinSet ? t('srb.pin.change') : t('srb.pin.setIt')}
          </Button>
        }
      >
        <TextField
          label={t('srb.pin.field')}
          hint={t('srb.pin.hint')}
          inputMode="numeric"
          autoComplete="off"
          type="password"
          maxLength={8}
          value={pin}
          error={errs['pin']}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          className="max-w-xs"
        />
        <ErrorLine error={Object.keys(errs).length ? null : save.error} />
      </Card>
    </form>
  );
}
